/**
 * Builder > Triggers > And/or selector
 * <ua-builder-items-triggers-logic>
 */

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { customElement, state } from 'lit/decorators.js';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x } from '@wordpress/i18n';

// Import recipe builder services
import { setTriggerLogic } from '@automator/builder/services/item';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'ua-builder-items-triggers-logic' )
export class TriggersAndOrSelector extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Update the component every time the logic changes
	@state() accessor logic = UncannyAutomator._recipe.triggers.logic;

	@state() accessor isSavingData = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		if ( UncannyAutomator._recipe.triggers.items.length >= 2 ) {
			return this.templateMultipleTriggers;
		}

		return this.templateDefault;
	}

	/**
	 * Renders the template used when there are no triggers or there is only one
	 *
	 * @return {TemplateResult} The template
	 */
	get templateDefault() {
		return html`
			<div class="header">
				<div class="header-title">${ escapeHTML( __( 'Trigger', 'uncanny-automator' ) ) }</div>
			</div>
		`;
	}

	/**
	 * Renders the template used when there are multiple triggers
	 *
	 * @return {TemplateResult} The template
	 */
	get templateMultipleTriggers() {
		// Create the dropdown
		const $dropdown = html`
			<uo-select
				@change=${ ( event ) => {
					this.logic = event.target.value;

					// Save the data
					this.saveData();
				} }
			>
				${ repeat(
					Object.entries( this.options ),
					// eslint-disable-next-line no-unused-vars
					( [ value, text ] ) => value,
					( [ value, text ] ) => html`
						<uo-select-option
							value="${ value }"
							text="${ text }"
							?selected=${ this.logic === value }
						></uo-select-option> 
					`
				) }
			</uo-select>
		`;

		return html`
			<div class="header">
				<div class="header-title">${ escapeHTML( __( 'Triggers', 'uncanny-automator' ) ) }</div>
				<div class="header-logic" dir="auto">
					<span class="header-logic-title">
						${ escapeHTML( _x( 'Run recipe when', 'Triggers logic', 'uncanny-automator' ) ) }
					</span>

					<span class="header-logic-dropdown">
						${ $dropdown }
					</span>

					<span class="header-logic-description">
						${ this.logic === 'any' ? escapeHTML( _x( 'of the following triggers is completed', 'Triggers logic', 'uncanny-automator' ) ) : escapeHTML( _x( 'of the following triggers are completed', 'Triggers logic', 'uncanny-automator' ) ) }
					</span>

					${ this.isSavingData ? html`<span class="header-loading"></span>` : '' }
				</div>
			</div>
		`;
	}

	/**
	 * Performs a REST call to save the selected
	 */
	saveData() {
		// Start loading animation
		this.isSavingData = true;

		setTriggerLogic(
			this.logic
		).then( response => {
			this.isSavingData = false;
		} ).catch( error => {
			this.isSavingData = false;
		} );
	}

	/**
	 * Returns the options for the dropdown
	 *
	 * @return {Object} where the keys are the option values
	 */
	get options() {
		const test = 'a';

		return {
			/* translators: Text of the "any" option. Full sentence: "ANY" of the following triggers is completed */
			any: escapeHTML( _x( 'Any', 'Triggers logic', 'uncanny-automator' ) ),
			/* translators: Text of the "all" option. Full sentence: "ALL" of the following triggers are completed */
			all: escapeHTML( _x( 'All', 'Triggers logic', 'uncanny-automator' ) ),
		}
	}
}
